/* eslint-disable @typescript-eslint/naming-convention */

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { useState } from 'react';
import { signInWithRedirect, signInWithPopup } from 'firebase/auth';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import Input from './atoms/Input';
import Button from './atoms/Button';
import {
  useCreateUserMutation,
  useLazyGetUserByFirebaseIdQuery,
} from '../store';
import UserDTO from '../models/user';
import { toastError } from '../utils/toasts';
import errorResponseToString from '../utils/errorResponseToString';
import config from '../config/config';
import { googleAuth, auth } from '../config/firebase-config';
import { emailRegex } from '../utils/regexs';

interface Props {
  onSuccess: (user: UserDTO) => void;
  onGoogle: () => void;
  mail: string;
}

interface FormData {
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
  phone_number: string;
}

const noSpaces = (value: string) => !/\s/.test(value);
const validationSchema = yup.object<FormData>().shape({
  name: yup
    .string()
    .required('Debes ingresar tu nombre'),
  email: yup
    .string()
    .required('Debes ingresar un correo')
    .matches(emailRegex, 'Este correo no está bien')
    .test('no-spaces', 'No se permiten espacios', noSpaces),
  password: yup
    .string()
    .required('Debes ingresar una contraseña')
    .test('no-spaces', 'No se permiten espacios', noSpaces),
  passwordConfirm: yup
    .string()
    .required('Por seguridad, debes confirmar tu contraseña')
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden')
    .test('no-spaces', 'No se permiten espacios', noSpaces),
  phone_number: yup.string()
    .required('Debes ingresar un número de teléfono')
    .test('is-valid-phone', 'Ingresá un celular válido', (value) => {
      if (!value) {
        return false;
      }
      const pn = parsePhoneNumberFromString(value);
      return !!pn && pn.isValid();
    }),
});

const SignUpForm: React.FC<Props> = ({ onSuccess, onGoogle, mail }) => {
  const [createUser, results] = useCreateUserMutation();
  const [errorMsg, setErrorMsg] = useState('');
  const form = useForm<FormData>({
    defaultValues: {
      email: mail,
      password: '',
      phone_number: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    register, handleSubmit, formState,
  } = form;

  const handleSignUp = async () => {
    const {
      name: username, email, password, phone_number,
    } = form.getValues();
    try {
      const response = await createUser({
        username, email, password, phone_number,
      });
      if ('error' in response) {
        return setErrorMsg(errorResponseToString(response.error));
      }
      if ('data' in response && response.data) {
        return onSuccess(response.data);
      }
      throw new Error(`no response data. response is ${JSON.stringify(response)}`);
    } catch (e) {
      console.error(e);
      return toastError('Ocurrió un error');
    }
  };

  const [getUserByFirebaseId] = useLazyGetUserByFirebaseIdQuery();
  const [firebaseAuthLoading, setFirebaseAuthLoading] = useState(false);

  const handleSignInWithGoogle = async () => {
    try {
      setFirebaseAuthLoading(true);
      if (config.env !== 'local') {
        return await signInWithRedirect(auth, googleAuth);
      }
      // local no funciona redirect
      const { user: fbUser } = await signInWithPopup(auth, googleAuth);
      const firebaseId = fbUser.uid;

      if (!firebaseId) {
        throw new Error('Failed to get Firebase ID');
      }
      if (!fbUser.email) {
        throw new Error('Google User has no email');
      }

      const { data: userByFbId } = await getUserByFirebaseId({ firebaseId });
      if (userByFbId) {
        onGoogle();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setFirebaseAuthLoading(false);
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center admin max-w-[820px] w-full">
      <form className="flex flex-col gap-6 w-full max-w-[358px]" onSubmit={handleSubmit(handleSignUp)}>
        <div className="flex justify-center align-items-center w-full">
          <Button
            loading={firebaseAuthLoading}
            disabled={firebaseAuthLoading}
            className="text-lg"
            type="button"
            onClick={handleSignInWithGoogle}
            variant="secondary"
          >
            <div className="flex items-center align-items-center">
              <img src="/google-icon.svg" alt="Google" className="w-5 h-5 mr-2" />
              <span>Crear cuenta con Google</span>
            </div>
          </Button>
        </div>
        <div className="flex justify-center w-full">
          <div className="flex items-center gap-4 w-full">
            <hr className="flex-grow border-border-primary" />
            <span className="text-text-tertiary">o</span>
            <hr className="flex-grow border-border-primary" />
          </div>
        </div>
        <Input
          label="Nombre *"
          placeholder="Ingresá tu nombre y apellido"
          labelAbove={true}
          id="name"
          {...register('name', {
            required: true,
          })}
          errMsg={formState.errors.name?.message}
        />
        <Input
          label="Correo *"
          placeholder="Ingresá tu correo electrónico"
          labelAbove={true}
          id="email"
          {...register('email', {
            required: true,
          })}
          errMsg={formState.errors.email?.message}
        />
        <Input
          label="Contraseña *"
          placeholder="Ingresá tu contraseña"
          labelAbove={true}
          id="password"
          type='password'
          {...register('password', {
            required: true,
          })}
          errMsg={formState.errors.password?.message}
        />
        <Input
          label="Confirmar contraseña *"
          placeholder="Confirmá tu contraseña"
          labelAbove={true}
          id="passwordConfirm"
          type='password'
          {...register('passwordConfirm', {
            required: true,
          })}
          errMsg={formState.errors.passwordConfirm?.message}
        />
        <Controller
          name="phone_number"
          control={form.control}
          render={({ field }) => (
            <Input
              label="Número de celular *"
              placeholder="Ingresá tu número de celular"
              labelAbove={true}
              id="phoneNumber"
              type='phone'
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value)}
              errMsg={formState.errors.phone_number?.message}
            />
          )}
        />
        {errorMsg && <p className="text-red-500">{errorMsg}</p>}
        <div className="flex justify-center mt-2 w-full">
          <Button
            loading={results.isLoading}
            className="text-lg"
            type="submit"
            variant="primary"
          >
            Crear usuario
          </Button>
        </div>
      </form>
    </div>
  );
};
export default SignUpForm;
